import { useTheme } from '../context/ThemeContext';
import { Check } from 'lucide-react';

const plans = [
  {
    name: 'Free',
    price: 'R0',
    period: 'forever',
    description: 'Everything you need to get started.',
    features: [
      'Up to 30 trips per month',
      'Business & Private categories',
      '1 vehicle',
      'Excel export',
      'Local storage',
    ],
    cta: 'Download Free',
    highlighted: false,
  },
  {
    name: 'Pro',
    price: 'R495',
    period: '/year',
    description: 'For professionals who need more.',
    features: [
      'Everything in Free',
      'Unlimited trips',
      'Unlimited vehicles',
      'Cloud sync & backup',
      'Saved locations',
      'Analytics dashboard',
      'Google addresses',
    ],
    cta: 'Get Started',
    highlighted: true,
  },
];

const Pricing = ({ onDownloadClick }) => {
  const theme = useTheme();

  return (
    <section
      id="pricing"
      className="py-32"
      style={{ backgroundColor: theme.background }}
    >
      <div className="container-app">

        {/* Header */}
        <div className="text-center mb-20">
          <h2
            className="text-responsive-lg font-bold tracking-tight mb-6"
            style={{ color: theme.text }}
          >
            Simple pricing.{' '}
            <span className="gradient-text">No surprises.</span>
          </h2>
          <p
            className="text-xl max-w-xl mx-auto"
            style={{ color: theme.textSecondary }}
          >
            Start free, upgrade when you're ready.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="flex flex-col md:flex-row justify-center items-center md:items-stretch gap-8 max-w-3xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`flex-1 max-w-sm w-full p-8 rounded-3xl relative ${
                plan.highlighted ? 'md:-mt-4 md:mb-4' : ''
              }`}
              style={{
                backgroundColor: theme.card,
                border: plan.highlighted ? 'none' : `1px solid ${theme.border}`,
                boxShadow: plan.highlighted ? '0 8px 32px rgba(0, 0, 0, 0.12)' : 'none',
              }}
            >
              {/* Gradient border for Pro */}
              {plan.highlighted && (
                <div
                  className="absolute inset-0 rounded-3xl bg-gradient-brand p-[2px]"
                  style={{ zIndex: -1 }}
                >
                  <div className="w-full h-full rounded-3xl" style={{ backgroundColor: theme.card }} />
                </div>
              )}

              {/* Popular badge */}
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 text-xs font-semibold text-white rounded-full bg-gradient-brand">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Plan name */}
              <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
                {plan.name}
              </h3>

              {/* Price */}
              <div className="mb-4">
                <span className="text-4xl font-bold" style={{ color: theme.text }}>
                  {plan.price}
                </span>
                <span className="text-base ml-1" style={{ color: theme.textMuted }}>
                  {plan.period}
                </span>
              </div>

              {/* Description */}
              <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
                {plan.description}
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: theme.primary }} />
                    <span className="text-sm" style={{ color: theme.textSecondary }}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={onDownloadClick}
                className={`block w-full text-center py-3 px-6 rounded-full font-medium transition-all duration-200 ${
                  plan.highlighted ? 'bg-gradient-brand text-white hover:opacity-90' : 'hover:opacity-80'
                }`}
                style={!plan.highlighted ? { backgroundColor: theme.cardAlt, color: theme.text } : {}}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <p className="text-center text-sm mt-12" style={{ color: theme.textMuted }}>
          Free tier is yours forever &middot; Upgrade or cancel anytime &middot; No questions asked.
        </p>

      </div>
    </section>
  );
};

export default Pricing;